// WHO SIGNS (Session AUJ). The quote goes to the person the office deals with; someone else often signs (a trustee,
// a director, a managing agent's principal). The office names that person here before the agreement goes, and the
// agreement, the Signable envelope, the welcome pack and the Client Portal login go to them. The quote's own contact
// is never changed by this, and stays in copy.
//
// Saves through PUT /api/quotes/:id/signatory. If the customer named someone themselves when accepting, their choice
// wins and the panel says so. Once the agreement has been sent for signature it cannot be changed (the server
// refuses it too, 409 envelope_sent).
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { QuoteSignatoryView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Loader } from "../../components/Loader";

function errorFrom(err: unknown, fallback: string): string {
  const first = err instanceof ApiError ? err.details?.signatory?.[0] : undefined;
  if (typeof first === "string") return first;
  return friendlyApiError(err, fallback);
}

export function QuoteSignatoryPanel({ quoteId }: { quoteId: string }) {
  const [view, setView] = useState<QuoteSignatoryView | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await api.quotes.signatory(quoteId);
      setView(v);
      setName(v.officeSignatory?.name ?? "");
      setEmail(v.officeSignatory?.email ?? "");
    } catch (err) {
      setError(friendlyApiError(err, "Who signs this quote could not be loaded."));
    }
  }, [quoteId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(clear: boolean) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.quotes.setSignatory(quoteId, clear ? null : { name: name.trim(), email: email.trim() });
      setView(res);
      setName(res.officeSignatory?.name ?? "");
      setEmail(res.officeSignatory?.email ?? "");
      setMessage(
        clear
          ? "Removed. The quote's contact will sign."
          : res.customerWins
            ? "Saved, but the customer named someone else when accepting, and their choice is used."
            : `Saved. The agreement will go to ${res.signer.name} to sign.`,
      );
    } catch (err) {
      setError(errorFrom(err, "Who signs could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  const canSave = name.trim().length > 0 && email.trim().length > 0 && !saving;

  return (
    <section aria-label="Who signs" className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">Who signs</h2>
      <p className="mt-0.5 text-xs text-ele-body opacity-70">
        Only needed when someone other than the quote&apos;s contact signs the agreement. The contact still receives the quote and stays in copy.
      </p>
      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="mt-3 text-sm text-ele-green">
          {message}
        </p>
      )}
      {!view ? (
        !error && <Loader shape="inline" className="mt-3" />
      ) : (
        <>
          <p className="mt-3 text-sm text-ele-body">
            The agreement {view.envelopeSent ? "was sent" : "will go"} to <span className="text-ele-strong">{view.signer.name}</span>
            {view.signer.email ? ` (${view.signer.email})` : ""}
            {view.signerSource === "customer" ? ", named by the customer when they accepted." : view.signerSource === "office" ? ", named by the office." : ", the quote's contact."}
          </p>
          {view.canChange ? (
            <>
              <label className="mt-4 block text-sm text-ele-body">
                Signatory&apos;s full name
                <input
                  type="text"
                  value={name}
                  maxLength={200}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
                />
              </label>
              <label className="mt-3 block text-sm text-ele-body">
                Signatory&apos;s email
                <input
                  type="email"
                  value={email}
                  maxLength={254}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
                />
              </label>
              <div className="mt-3 flex items-center justify-end gap-2">
                {view.officeSignatory && (
                  <button
                    type="button"
                    onClick={() => void save(true)}
                    disabled={saving}
                    className="rounded-ele border border-ele-hairline px-3 py-1.5 text-xs text-ele-body hover:text-ele-strong disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void save(false)}
                  disabled={!canSave}
                  className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body hover:text-ele-strong disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save signatory"}
                </button>
              </div>
            </>
          ) : (
            <p className="mt-2 text-xs text-ele-faint">
              The agreement has been sent for signature, so who signs can no longer be changed here.
            </p>
          )}
        </>
      )}
    </section>
  );
}
